import { navigateAddModal, navigateRemoveModal } from 'client/actions/navigation'
import {
  loadPayNowAuthorizationStatus,
  savePayNowAuthorization,
  updatePayNowStatus,
} from 'client/actions/payNowManagement'
import Button, { ButtonType } from 'client/components/common/Button/Button'
import { notifier } from 'client/components/common/Notifier'
import { parsePropsFromQueryParams, PayNowHelper } from 'client/components/paynow/Paynow.helper'
import { Analytics, checkPlacement } from 'client/Global/Analytics'
import { AnalyticsType, Category, Events, Placement, Result } from 'client/Global/AnalyticsEvents'
import {
  PayNowAuthorizationFailure,
  PayNowAuthorizationSucess,
  PayNowInterface,
} from 'client/Interfaces/PayNow/AccountAuthorizationParams'
import { SettingsInterface } from 'client/Interfaces/Settings/SettingsInterface'
import { StateInterface } from 'client/Interfaces/StateInterface'
import { cleanString } from 'client/Other/functions/stringFunctions'
import { l } from 'client/Other/Translation'
import { setBaseClass } from 'client/utils'
import * as React from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import * as SettingsActions from '../../../../actions/settings'
import PaymentDetailsForm from '../../../Modal/CompanyInformationModal/PaymentDetailsForm'
import '../CompanyInformation/CompanyInformation.scss'

interface PropsType {
  paymentMethodsSchema: any
  paymentMethodsValues: any
  vatOptions: any
  locale: string
  saveData: (data, paymentValuesChangedFields) => void
  next: () => void
  onSave?: () => void
  openActivatePayNowModal: (originUrl: string) => void
  payNow: PayNowInterface
  togglePayNowStatus: (status: boolean) => Promise<any>
  openPayNowFailureModal: () => void
  openPayNowSuccessModal: () => void
  saveAuthorization: (authorizationParams: PayNowAuthorizationSucess) => Promise<any>
  loadAuthorizationStatus: () => Promise<any>
  authorizationFailure: PayNowAuthorizationFailure
  authorizationSuccess: PayNowAuthorizationSucess
  country?: string
  stateSettings: SettingsInterface
  location?: {
    search: string
  }
}

interface StateType {
  paymentValues?: any
  paymentValuesChangedFields?: string[]
  autoFilledFields?: any
  isSaving: boolean
}

const cls = setBaseClass('PaymentInformationSettings')
let ibanValidationResult = true
let swiftValidationResult = true

class PaymentInformationClass extends React.Component<PropsType, StateType> {
  constructor(props) {
    super(props)
    this.state = {
      isSaving: false,
    }
  }

  componentDidMount(): void {
    const {
      authorizationFailure,
      authorizationSuccess,
      saveAuthorization,
      openPayNowFailureModal,
      loadAuthorizationStatus,
    } = this.props
    if (authorizationFailure) {
      Analytics.trackEvent(Events.olpSignup, {
        type: AnalyticsType.action,
        category: Category.onlinePayment,
        result: Result.cancel,
        placement: checkPlacement(window.location.pathname),
      })
      openPayNowFailureModal()
    } else if (authorizationSuccess) {
      Analytics.trackEvent(Events.olpSignup, {
        type: AnalyticsType.action,
        category: Category.onlinePayment,
        result: Result.success,
        placement: checkPlacement(window.location.pathname),
      })
      if (PayNowHelper.validateAndConsumeCSRFToken(authorizationSuccess.state)) {
        void saveAuthorization(authorizationSuccess).then(() => {
          if (this.props.payNow.status.connected) {
            this.props.openPayNowSuccessModal()
          } else {
            openPayNowFailureModal()
          }
        })
      } else {
        openPayNowFailureModal()
      }
    } else {
      void loadAuthorizationStatus()
    }
  }

  private onPaymentValuesChange = (newValues, changedFields) => {
    this.setState({
      paymentValues: newValues,
      paymentValuesChangedFields: changedFields,
    })
  }

  private constructPaymentDetailsValues() {
    if (!this.state.paymentValues) {
      return
    }
    return this.state.paymentValues
  }

  private getIbans(validationResult) {
    ibanValidationResult = validationResult
  }

  private getSwifts(validationResult) {
    swiftValidationResult = validationResult
  }

  private onSave = async () => {
    if (ibanValidationResult && swiftValidationResult) {
      const paymentDetailsValues = this.constructPaymentDetailsValues()
      this.setState({ isSaving: true })
      await this.props.saveData(paymentDetailsValues, this.state.paymentValuesChangedFields)
      this.setState({ isSaving: false }, () => {
        notifier('SettingsSaved', 'success')
      })
    }
  }

  public render() {
    return (
      <div className={cls()}>
        <div className={cls('__header-section')}>
          <h1>{l('myAccount.paymentMethods')}</h1>
        </div>
        <div className={cls('__content-section')}>
          <PaymentDetailsForm
            schema={this.props.paymentMethodsSchema}
            values={this.props.paymentMethodsValues}
            onChange={(vals, changedFields) => this.onPaymentValuesChange(vals, changedFields)}
            onOpenActivatePayNowModal={() => this.props.openActivatePayNowModal('/myAccount/paymentMethods')}
            payNow={this.props.payNow}
            togglePayNowStatus={this.props.togglePayNowStatus}
            // eslint-disable-next-line @typescript-eslint/unbound-method
            getIbans={this.getIbans}
            getSwifts={this.getSwifts}
            locale={this.props.locale}
            stateSettings={this.props.stateSettings}
          />
        </div>
        <div className={cls('__footer-section')}>
          <Button
            label={l('save')}
            type={ButtonType.accept}
            action={this.onSave}
            showLoadingAnimation={this.state.isSaving}
          />
        </div>
      </div>
    )
  }
}

const closeModal = (dispatch, modalName: string) => () => dispatch(navigateRemoveModal(modalName))

const openModal = (dispatch, modalName: string) => () => {
  return dispatch(
    navigateAddModal({
      component: modalName,
      props: { close: closeModal(dispatch, modalName) },
    }),
  )
}

const mapStateToProps = (state: StateInterface, { location }) => {
  return {
    ...parsePropsFromQueryParams(location ? location.search : ''),
    paymentMethodsSchema: state.settings.schemas.paymentMethods,
    paymentMethodsValues: state.settings.values.paymentMethods,
    locale: state.token.locale,
    payNow: state.payNow,
    country: state.company.country,
    stateSettings: state.settings,
  }
}

export const cleanPaymentDetails = (paymentDetailsValues) => {
  if (!paymentDetailsValues) {
    return null
  }
  const cleanStringValsInObj = (obj) => {
    Object.entries(obj).forEach(([k, v]) => {
      obj[k] = cleanString(v)
    })
  }
  const compactIBANs = (IBANs) =>
    IBANs.filter(({ bank, iban, swift }) => {
      return Boolean(bank || iban || swift)
    })
  const newValues = paymentDetailsValues.map(({ type, fields, _id, optionalIBANs }) => ({
    type,
    fields: { ...fields },
    _id,
    ...(optionalIBANs ? { optionalIBANs: compactIBANs(optionalIBANs) } : {}),
  }))
  newValues.forEach(({ fields, optionalIBANs }) => {
    cleanStringValsInObj(fields)
    if (optionalIBANs) {
      optionalIBANs.forEach((optIBANFields) => {
        cleanStringValsInObj(optIBANFields)
      })
    }
  })
  return newValues
}

const mapDispatchToProps = (dispatch, state) => ({
  saveData: async (paymentDetailsValues, changedFields) => {
    const paymentMethods = cleanPaymentDetails(paymentDetailsValues)
    const settings = {
      ...(paymentMethods ? { paymentMethods } : {}),
    }
    await dispatch(SettingsActions.updateSettings(settings))
    Analytics.trackEvent(Events.editSettings, {
      type: AnalyticsType.action,
      category: Category.setting,
      result: Result.success,
      placement: Placement.myAccount,
      tab: 'paymentMethodsSettings',
      changedFields,
    })
  },
  openActivatePayNowModal: (originUrl) => {
    Analytics.trackEvent(Events.olpSignupModal, {
      type: AnalyticsType.modal,
      category: Category.onlinePayment,
      result: Result.open,
      placement: checkPlacement(window.location.pathname),
    })
    dispatch(
      navigateAddModal({
        component: 'ActivatePayNowModal',
        props: {
          isOpen: true,
          originUrl,
        },
      }),
    )
  },
  togglePayNowStatus: (status: boolean) => dispatch(updatePayNowStatus({ active: status })),
  openPayNowFailureModal: openModal(dispatch, 'PayNowAuthorizationFailureModal'),
  openPayNowSuccessModal: openModal(dispatch, 'PayNowAuthorizationSuccessModal'),
  saveAuthorization: (authorizationParams: PayNowAuthorizationSucess) =>
    dispatch(savePayNowAuthorization(authorizationParams)),
  loadAuthorizationStatus: () => dispatch(loadPayNowAuthorizationStatus()),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(PaymentInformationClass))
